import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { Receipt, Camera, Loader2, Clock, Check, X, Plus, Image as ImageIcon } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { api, fileUrl } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

export const PortalPurchases = () => {
  const { t } = useLang();
  const cur = t("portal.currency");
  const money = (n) => `${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} ${cur}`;
  const STATUS = {
    pending: { label: t("portal.purchases.stPending"), cls: "bg-amber-100 text-amber-800", Icon: Clock },
    approved: { label: t("portal.purchases.stApproved"), cls: "bg-emerald-100 text-emerald-700", Icon: Check },
    rejected: { label: t("portal.purchases.stRejected"), cls: "bg-rose-100 text-rose-700", Icon: X },
  };

  const [rows, setRows] = useState(null);
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const load = () => api.myPurchases().then((d) => setRows(d.purchases || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error(t("portal.purchases.enterAmount")); return; }
    if (!file) { toast.error(t("portal.purchases.attach")); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("amount", amt);
    fd.append("description", desc);
    fd.append("month", new Date().toISOString().slice(0, 7));
    fd.append("file", file);
    try {
      await api.submitPurchase(fd);
      toast.success(t("portal.purchases.sent"));
      setAmount(""); setDesc(""); setFile(null); setPreview("");
      if (fileRef.current) fileRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("portal.purchases.sendFail")); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="portal-purchases">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3">
        <p className="font-heading font-bold text-slate-800 flex items-center gap-1.5"><Receipt className="h-5 w-5 text-blue-600" /> {t("portal.purchases.title")}</p>
        <p className="text-[11px] text-slate-400">{t("portal.purchases.sub")}</p>

        <div className="w-full rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 overflow-hidden" data-testid="purchase-preview-box">
          {preview ? (
            <img src={preview} alt="" className="w-full max-h-56 object-contain" />
          ) : (
            <div className="py-8 flex flex-col items-center gap-2 text-slate-400">
              <Receipt className="h-8 w-8" />
              <span className="text-sm font-semibold">{t("portal.purchases.attach")}</span>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} data-testid="purchase-camera-btn"
            className="h-10 gap-1.5 border-slate-300"><Camera className="h-4 w-4" /> {t("portal.purchases.capture")}</Button>
          <Button type="button" variant="outline" onClick={() => galleryRef.current?.click()} data-testid="purchase-gallery-btn"
            className="h-10 gap-1.5 border-slate-300"><ImageIcon className="h-4 w-4" /> {t("portal.purchases.gallery")}</Button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid="purchase-file-input" />
        <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={pick} data-testid="purchase-gallery-input" />

        <div className="grid grid-cols-2 gap-2">
          <label className="block"><span className="text-[11px] text-slate-500">{t("portal.purchases.amount")} ({cur})</span>
            <Input type="number" dir="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} className="h-10 font-mono-nums" data-testid="purchase-amount" /></label>
          <div className="flex items-end">
            <Button onClick={submit} disabled={busy} data-testid="purchase-submit" className="w-full h-10 bg-blue-600 hover:bg-blue-700 gap-1.5">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("portal.purchases.send")}</Button>
          </div>
        </div>
        <label className="block"><span className="text-[11px] text-slate-500">{t("portal.purchases.descOptional")}</span>
          <Textarea rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("portal.purchases.descPh")} data-testid="purchase-desc" /></label>
      </div>

      <div>
        <p className="font-heading font-bold text-slate-800 mb-2">{t("portal.purchases.prev")}</p>
        {!rows ? <div className="py-8 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
          : rows.length === 0 ? <p className="py-8 text-center text-slate-400 text-sm" data-testid="portal-purchases-empty">{t("portal.purchases.empty")}</p>
            : (
              <div className="space-y-2">
                {rows.map((it) => {
                  const st = STATUS[it.status] || STATUS.pending;
                  return (
                    <div key={it.id} data-testid={`my-purchase-${it.id}`} className="flex items-center gap-3 rounded-xl border border-slate-100 bg-white p-2.5">
                      <a href={fileUrl(it.photo_id)} target="_blank" rel="noreferrer" className="h-12 w-12 shrink-0 rounded-lg overflow-hidden bg-slate-100">
                        <img src={fileUrl(it.photo_id)} alt="" className="h-full w-full object-cover" />
                      </a>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between"><span className="font-mono-nums font-bold text-slate-800">{money(it.amount)}</span>
                          <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-semibold px-2 py-0.5 ${st.cls}`}><st.Icon className="h-3 w-3" />{st.label}</span></div>
                        {it.description && <p className="text-xs text-slate-500 truncate">{it.description}</p>}
                        <p className="text-[10px] text-slate-400">{t("portal.purchases.month", { m: it.month })} · {(it.created_at || "").slice(0, 10)}</p>
                        {it.status === "rejected" && it.note && <p className="text-[11px] text-rose-500">{t("portal.purchases.rejectReason", { r: it.note })}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
      </div>
    </div>
  );
};
